import { Upload, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAuthStore } from "@/features/auth/store";
import ReviewChecklistCard from "@/features/guide/ReviewChecklistCard";
import { useStatsToday } from "@/features/stats/useStatsToday";

export default function HomePage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const statsQuery = useStatsToday();

  // 카드 정의는 ``i18nKey`` 만 보유 — 라벨은 렌더 시점에 t() 로 해석.
  // 정의 자체가 언어에 의존하지 않도록 분리 (Phase 1 정책).
  const cards = (() => {
    const STAT_KEYS = {
      processed: "dashboard.stats.processedToday",
      success: "dashboard.stats.success",
      rejected: "dashboard.stats.rejectedAndError",
    } as const;

    if (statsQuery.isLoading) {
      return [
        { i18nKey: STAT_KEYS.processed, value: "—", icon: FileText, tone: "neutral" as const },
        { i18nKey: STAT_KEYS.success, value: "—", icon: CheckCircle2, tone: "neutral" as const },
        {
          i18nKey: STAT_KEYS.rejected,
          value: "—",
          icon: AlertTriangle,
          tone: "neutral" as const,
        },
      ];
    }
    if (statsQuery.error) {
      // "ERR" 은 사용자 노출용 디버그 인디케이터 — 언어와 무관하게 유지.
      return [
        { i18nKey: STAT_KEYS.processed, value: "ERR", icon: FileText, tone: "error" as const },
        { i18nKey: STAT_KEYS.success, value: "ERR", icon: CheckCircle2, tone: "error" as const },
        {
          i18nKey: STAT_KEYS.rejected,
          value: "ERR",
          icon: AlertTriangle,
          tone: "error" as const,
        },
      ];
    }
    const s = statsQuery.data;
    const rejectedAndError = (s?.rejected_count ?? 0) + (s?.error_count ?? 0);
    return [
      {
        i18nKey: STAT_KEYS.processed,
        value: String(s?.processed_count ?? 0),
        icon: FileText,
        tone: "neutral" as const,
      },
      {
        i18nKey: STAT_KEYS.success,
        value: String(s?.success_count ?? 0),
        icon: CheckCircle2,
        tone: "success" as const,
      },
      {
        i18nKey: STAT_KEYS.rejected,
        value: String(rejectedAndError),
        icon: AlertTriangle,
        tone: rejectedAndError > 0 ? ("warn" as const) : ("neutral" as const),
      },
    ];
  })();

  const name = user?.email.split("@")[0];

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">
          {name ? t("dashboard.greetingNamed", { name }) : t("dashboard.greetingAnon")}
        </h1>
        <p className="mt-1 text-sm text-slate-500">{t("dashboard.description")}</p>
      </div>

      {/* Quick action */}
      <Link
        to="/upload"
        className="group flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-6 py-5 transition hover:border-blue-300 hover:bg-blue-100"
      >
        <div>
          <div className="flex items-center gap-2 text-blue-900">
            <Upload className="h-5 w-5" />
            <span className="text-base font-semibold">{t("dashboard.quickAction.title")}</span>
          </div>
          <p className="mt-1 text-sm text-blue-700/80">{t("dashboard.quickAction.description")}</p>
        </div>
        <span className="text-blue-700 transition group-hover:translate-x-1">→</span>
      </Link>

      {/* 검수 체크리스트 — 작업 진입 시 상시 리마인드 */}
      <div className="mt-6">
        <ReviewChecklistCard />
      </div>

      {/* 오늘 통계 — GET /api/stats/today */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map(({ i18nKey, value, icon: Icon, tone }) => (
          <div
            key={i18nKey}
            className={
              "rounded-lg border bg-white p-5 " +
              (tone === "error"
                ? "border-red-200"
                : tone === "warn"
                  ? "border-amber-200"
                  : tone === "success"
                    ? "border-emerald-200"
                    : "border-slate-200")
            }
          >
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-slate-400">{t(i18nKey)}</span>
              <Icon
                className={
                  "h-4 w-4 " +
                  (tone === "error"
                    ? "text-red-400"
                    : tone === "warn"
                      ? "text-amber-500"
                      : tone === "success"
                        ? "text-emerald-500"
                        : "text-slate-300")
                }
              />
            </div>
            <p
              className={
                "mt-2 text-2xl font-bold " + (tone === "error" ? "text-red-600" : "text-slate-900")
              }
            >
              {value}
            </p>
          </div>
        ))}
      </div>

      <p className="mt-4 text-xs text-slate-400">{t("dashboard.statsFootnote")}</p>
    </div>
  );
}
